import { AssistantFilterHighlightKind } from 'utils/assistantChatStore';

export type AssistantHighlightKind =
  | AssistantFilterHighlightKind
  | 'search'
  | 'list'
  | 'orders-actions'
  | 'orders-tabs';

const SELECTORS: Record<AssistantHighlightKind, string[]> = {
  stars: ['[data-assistant-target="stars"]', '.hotel-result-filter-input.star.star2'],
  price: [
    '[data-assistant-target="price"]',
    '.hotel-result-filter-input.prices',
    '.flight-filter [data-assistant-target="price"]',
  ],
  location: ['[data-assistant-target="location"]', '.hotel-result-filter-input.location'],
  search: [
    '[data-assistant-target="search"]',
    '.hotel-search-wr',
    '.search-wrapper.hotel-search-wr',
    '.search-wrapper.flight-search',
  ],
  sort: [
    '[data-assistant-target="sort"]',
    '.hotel-result-filter-row.hotel-result-sort-by',
    '.results-sort-by',
  ],
  displayMode: ['[data-assistant-target="displayMode"]', '.hotel-display-mode-body'],
  rooms: ['[data-assistant-target="rooms"]', '.hotel-result-filter-left'],
  suppliers: ['[data-assistant-target="suppliers"]', '.hotel-result-filter-input.supp'],
  facilities: ['[data-assistant-target="facilities"]', '.hotel-result-filter-left'],
  name: ['[data-assistant-target="name"]', '.hotel-result-filter-left'],
  meal: ['[data-assistant-target="meal"]', '.hotel-result-filter-input.star:not(.star2)'],
  additional: ['[data-assistant-target="additional"]', '.hotel-result-filter-left'],
  stops: ['[data-assistant-target="stops"]'],
  baggage: ['[data-assistant-target="baggage"]'],
  times: ['[data-assistant-target="times"]'],
  duration: ['[data-assistant-target="duration"]'],
  airlines: ['[data-assistant-target="airlines"]'],
  airports: ['[data-assistant-target="airports"]'],
  flightTypes: ['[data-assistant-target="flightTypes"]'],
  list: [
    '[data-assistant-target="list"]',
    '.hotel-list-wr',
    '.hotel-list',
    '.flight-result-list',
    '.flight-results',
  ],
  'orders-actions': ['[data-assistant-target="orders-actions"]', '.mo-actions-row'],
  'orders-tabs': ['[data-assistant-target="orders-tabs"]', '.mo-tabs2', '.mo-tabs-wr'],
};

const LAYER_ID = 'assistant-highlight-layer';
const PULSE_MS = 1100;

const ensureLayer = () => {
  let layer = document.getElementById(LAYER_ID);
  if (!layer) {
    layer = document.createElement('div');
    layer.id = LAYER_ID;
    layer.className = 'assistant-highlight-layer';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
  }
  return layer;
};

const isVisibleTarget = (el: HTMLElement) => {
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  const rect = el.getBoundingClientRect();
  return rect.width >= 2 && rect.height >= 2;
};

const resolveElement = (kind: AssistantHighlightKind) => {
  for (const selector of SELECTORS[kind]) {
    const nodes = document.querySelectorAll(selector);
    for (let i = 0; i < nodes.length; i += 1) {
      const el = nodes[i];
      if (el instanceof HTMLElement && isVisibleTarget(el)) {
        return el;
      }
    }
  }
  return null;
};

const placePulse = (el: HTMLElement, layer: HTMLElement, index: number) => {
  const rect = el.getBoundingClientRect();
  const pad = 6;
  const pulse = document.createElement('div');
  pulse.className = 'assistant-highlight-pulse';
  pulse.style.top = `${Math.max(0, rect.top - pad)}px`;
  pulse.style.left = `${Math.max(0, rect.left - pad)}px`;
  pulse.style.width = `${rect.width + pad * 2}px`;
  pulse.style.height = `${rect.height + pad * 2}px`;
  pulse.style.animationDelay = `${index * 70}ms`;
  layer.appendChild(pulse);

  window.setTimeout(() => {
    pulse.remove();
    if (!layer.childElementCount) {
      layer.remove();
    }
  }, PULSE_MS + index * 70);
};

export const highlightAssistantTargets = (kinds: AssistantHighlightKind[]) => {
  const unique = Array.from(new Set(kinds.filter(Boolean)));
  if (!unique.length || typeof document === 'undefined') {
    return;
  }

  window.setTimeout(() => {
    const layer = ensureLayer();
    const seen = new Set<HTMLElement>();

    unique.forEach((kind, index) => {
      const el = resolveElement(kind);
      if (!el || seen.has(el)) {
        return;
      }
      seen.add(el);

      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      window.setTimeout(() => placePulse(el, layer, index), 120);
    });
  }, 50);
};
